import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Stack,
  TextField,
} from "@mui/material";
import React from "react";
import useBoolean from "../hooks/useBoolean";
import { LoadingButton } from "@mui/lab";
import { chunkCSVExport } from "../utils/csvUtils";
import { InvestorAmlOverview } from "../api/transformers/aml";
import { fullName } from "../utils/investor";
import { parse, format } from "date-fns";
import { useSnackbar } from "notistack";
import { AmlAPI } from "../api";
import { FilterValue } from "./Filter";

type ExportDialogProps = {
  open: boolean;
  filter: FilterValue;
  onClose: () => void;
};

const ExportDialog: React.FC<ExportDialogProps> = ({
  open,
  filter,
  onClose,
}) => {
  const [
    downloading,
    { setTrue: startDownloading, setFalse: finishDownloading },
  ] = useBoolean(false);
  const [filename, setFilename] = React.useState("");
  const [progress, setProgress] = React.useState(0);
  const { enqueueSnackbar } = useSnackbar();
  const abortHandler = React.useRef<AbortController | undefined>();
  React.useEffect(() => {
    if (open) {
      setFilename(getFilenamePlaceholder());
    }
  }, [open]);
  const handleDownload = () => {
    startDownloading();
    abortHandler.current = new AbortController();
    chunkCSVExport<InvestorAmlOverview>(
      (skip, limit) => {
        return AmlAPI.getReviewList({
          ...filter,
          skip: skip + "",
          limit: limit + "",
        }).then((response) => {
          return {
            total: response.total,
            data: response.rows,
          };
        });
      },
      {
        filename: filename || getFilenamePlaceholder(),
        fields,
        signal: abortHandler.current.signal,
        onProgressUpdate: (current, total) =>
          setProgress(() => Math.floor((current / total) * 100)),
      }
    )
      .then(() => {
        onClose();
      })
      .catch((err) => {
        console.error(err);
        // ignore AbortError
        if (err.name !== "AbortError") {
          enqueueSnackbar("An error occurred during export.", {
            variant: "error",
          });
        }
      })
      .finally(() => {
        setProgress(0);
        finishDownloading();
      });
  };
  const handleExportCancel = () => {
    finishDownloading();
    enqueueSnackbar("Export cancelled.", { variant: "info" });
    abortHandler.current?.abort();
    onClose();
  };
  const getFilenamePlaceholder = () => {
    return `export_${format(new Date(), "yyyy-MM-dd")}`;
  };
  return (
    <Dialog
      open={open}
      fullWidth
      onClose={(_, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
      PaperProps={{
        sx: {
          p: 0,
        },
      }}
    >
      <DialogTitle sx={{ fontSize: "1.2rem" }}>Export as CSV</DialogTitle>
      <DialogContent>
        <Stack direction="row" columnGap="20px" sx={{ margin: 2 }}>
          <TextField
            size="small"
            label="Filename"
            value={filename}
            sx={{ flexGrow: 1 }}
            onChange={(e) => setFilename(e.target.value)}
          />
          <LoadingButton
            variant="contained"
            color="primary"
            loading={downloading}
            onClick={handleDownload}
            sx={{
              position: "relative",
              maxWidth: 120,
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                borderRadius: "99px",
                height: "100%",
                width: `${progress}%`,
                backgroundColor: "#82aa73",
                transition: "width 0.3s ease",
                zIndex: 1,
              },
              "& > *": {
                position: "relative",
                zIndex: 2,
              },
            }}
          >
            Download
          </LoadingButton>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          variant="text"
          sx={{
            backgroundColor: "transparent",
            color: "primary.main",
            padding: "6px 8px",
            minWidth: "64px",
            fontSize: "0.875rem",
            fontWeight: 500,
            lineHeight: 1.75,
            borderRadius: "4px",
            textTransform: "uppercase",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.04)",
              boxShadow: "none",
            },
          }}
          onClick={handleExportCancel}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const fields: {
  label: string;
  value: keyof InvestorAmlOverview | ((r: InvestorAmlOverview) => string);
}[] = [
  {
    label: "Person",
    value: (record) => fullName(record),
  },
  {
    label: "Person Code",
    value: "personId",
  },
  {
    label: "Onboarding Account",
    value: "onboardingAccountId",
  },
  {
    label: "Onboarding Date",
    value: (record) =>
      format(
        parse(record.onboardingDate, "yyyyMMdd", new Date()),
        "dd/MM/yyyy"
      ),
  },
  {
    label: "ID&V",
    value: (record) => record.idvDescription || "-",
  },
  {
    label: "PEP",
    value: (record) => record.pepDescription || "-",
  },
  {
    label: "Sanctions",
    value: (record) => record.sanctionsDescription || "-",
  },
  {
    label: "Risk Rating",
    value: (record) => record.riskRatingDescription || "-",
  },
  {
    label: "Assignee",
    value: (record) => record.assignee || "Unassigned",
  },
];

export default ExportDialog;
